import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { 
  Users, 
  Filter, 
  RefreshCw,
  Shield,
  DollarSign
} from 'lucide-react';

const LevelIncome = () => {
  const [levelIncomes, setLevelIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [stats, setStats] = useState({
    totalCommission: 0,
    todayCommission: 0,
    blockedCommission: 0,
    level1: 0, level2: 0, level3: 0, level4: 0, level5: 0
  });

  const userDbKey = localStorage.getItem('dbKey');
  const userRegistrationDate = localStorage.getItem('registrationDate');

  useEffect(() => {
    if (userDbKey) {
      fetchLevelIncomes();
    }
  }, [userDbKey]);

  const fetchLevelIncomes = async () => {
    try {
      setLoading(true);
      
      const levelRef = ref(database, 'levelIncomes');
      const snapshot = await get(levelRef);
      
      const incomes = [];
      let totalCommission = 0;
      let todayCommission = 0;
      let blockedCommission = 0;
      const levelCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      const today = new Date().toISOString().split('T')[0];
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.entries(data).forEach(([date, users]) => {
          if (users[userDbKey]) {
            Object.entries(users[userDbKey]).forEach(([incomeId, income]) => {
              const amount = parseFloat(income.amount) || 0;
              const level = parseInt(income.level) || 1;
              const isBlocked = income.status === 'blocked';
              
              const incomeItem = {
                id: incomeId,
                date: date,
                level: level,
                amount: amount,
                status: income.status || 'credited',
                fromUser: income.fromUserName,
                fromUserId: income.fromUserId,
                packageName: income.packageName,
                description: income.description,
                timestamp: income.date || income.createdAt,
                isBlocked: isBlocked
              };
              
              incomes.push(incomeItem);
              
              if (!isBlocked) {
                totalCommission += amount;
                levelCounts[level] = (levelCounts[level] || 0) + amount;
                if (date === today) todayCommission += amount;
              } else {
                blockedCommission += amount;
              }
            });
          }
        });
      }
      
      // Sort by date (newest first)
      incomes.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setLevelIncomes(incomes);
      setStats({
        totalCommission,
        todayCommission,
        blockedCommission,
        level1: levelCounts[1],
        level2: levelCounts[2],
        level3: levelCounts[3],
        level4: levelCounts[4],
        level5: levelCounts[5]
      });
    } catch (error) {
      console.error('Error fetching level incomes:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLevelBadge = (level) => {
    const colors = [
      'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'bg-green-500/20 text-green-400 border-green-500/30',
      'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'bg-red-500/20 text-red-400 border-red-500/30'
    ];
    const colorIndex = Math.min(level - 1, 4);
    return colors[colorIndex] || colors[0];
  };

  const getStatusBadge = (status) => {
    if (status === 'blocked') {
      return <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 border border-red-500/30">
        <Shield className="h-3 w-3 mr-1" />
        Blocked
      </span>;
    }
    return <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 border border-green-500/30">
      Active
    </span>;
  };

  const filterIncomes = () => {
    let filtered = levelIncomes;
    
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date || item.timestamp);
        switch(dateFilter) {
          case 'today':
            return itemDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return itemDate > weekAgo;
          case 'month':
            const monthAgo = new Date();
            monthAgo.setDate(monthAgo.getDate() - 30);
            return itemDate > monthAgo;
          default:
            return true;
        }
      });
    }
    
    if (levelFilter !== 'all') {
      filtered = filtered.filter(item => item.level === parseInt(levelFilter));
    }
    
    return filtered;
  };

  const filteredIncomes = filterIncomes();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-2xl font-bold text-white mb-2">Level Income</h1>
      <p className="text-gray-400 mb-6">Your level commission earnings</p>

      {/* Level Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalCommission)}</div>
        </div>
        {[1, 2, 3, 4, 5].map(level => (
          <div key={level} className="card-glow p-3 bg-gray-800/50 rounded-xl">
            <div className="text-gray-400 text-xs mb-1">Level {level}</div>
            <div className="text-lg font-bold text-blue-400">{formatCurrency(stats[`level${level}`])}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card-glow p-4 mb-6 bg-gray-800/50 rounded-xl">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex gap-2">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="all">All Levels</option>
              <option value="1">Level 1</option>
              <option value="2">Level 2</option>
              <option value="3">Level 3</option>
              <option value="4">Level 4</option>
              <option value="5">Level 5</option>
            </select>
            
            <button
              onClick={fetchLevelIncomes}
              className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Incomes List */}
      <div className="space-y-4">
        {filteredIncomes.length > 0 ? (
          filteredIncomes.map((income) => {
            const isBlocked = income.status === 'blocked';
            
            return (
              <div key={income.id} className="card-glow p-4 bg-gray-800/50 rounded-xl">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isBlocked ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                      <Users className={`h-5 w-5 ${isBlocked ? 'text-red-400' : 'text-green-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs border ${getLevelBadge(income.level)}`}>
                          Level {income.level}
                        </span>
                        {getStatusBadge(income.status)}
                      </div>
                      <p className="text-gray-300 text-sm">{income.description}</p>
                      <p className="text-gray-400 text-xs mt-1">
                        From: {income.fromUser || 'Unknown'} • {formatDate(income.timestamp)}
                      </p>
                      {income.packageName && (
                        <p className="text-gray-500 text-xs mt-1">Package: {income.packageName}</p>
                      )}
                    </div>
                  </div>
                  <div className={`text-lg font-bold ${isBlocked ? 'text-red-400' : 'text-green-400'}`}>
                    {isBlocked ? '-' : '+'}{formatCurrency(income.amount)}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 card-glow bg-gray-800/50 rounded-xl">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-white mb-2">No Level Incomes</h3>
            <p className="text-gray-400">You haven't received any level commissions yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LevelIncome;
